/**
 * AdminLayout — Elreign tenant-admin shell (Pieter's surface).
 *
 * v2 sidebar: Operate / Configure sections, brand version pip v2.4.1,
 * My queue, Clients (Client Book), FICA pipeline items; v2 topbar with
 * date pill + Cmd-K.
 *
 * Design reference: paul-handoff/design-iterations/claude-design-output-admin-v2/
 *   elreign/project/Pieter · Admin Surface v2.html
 */

import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ReignMark } from '../reign-mark';
import { FirmLogo } from '../firm-logo';
import { FirmMonogram } from '../firm-monogram';
import { Icon } from '../icon';
import type { IconName } from '../icon';
import { useActiveBranding } from '../../lib/use-active-branding';
import { resolveBrandLockup } from '../../lib/brand-lockup';
import { portalHostForTenant } from '../../lib/tenant';
import {
  useOperatorTenantMe,
  useBranding,
  useOperatorMe,
  useAdminDsarList,
  useAdminRiskReview,
  useAdminRecoveryRequests,
  useTrusts,
  useFirmBookSummary,
} from '../../lib/api';
import { useSession } from '../../lib/use-session';
import { labelForRole } from '../../lib/labels';
import { normalizeRole, type Role } from '../../lib/authz/route-policy';
import {
  canUseAdminOrComplianceTier,
  canUseWorklistTier,
  canReadWholeBook,
  canReadFirmSettings,
} from '../../lib/authz/firm-capabilities';
import { ficaDaysOutOf, isInitialFicaOutstanding } from '../../lib/fica-review';
import { useAdminTheme } from '../../lib/use-admin-theme';
import { useIsStandingIn } from '../../lib/acting/context';
import { TenantSwitcher } from '../header/tenant-switcher';
import { useSyncMyTenants } from '../../lib/use-sync-my-tenants';
import { signOutAndRedirect } from '../../lib/sign-out';
import { SkipToContentLink } from '../ui/skip-link';
// BEL-01's missing half. The client shell's bell has rendered toast history
// since 25 August; this shell rendered none, so on /admin/team, the one screen
// where an invite can fail, a dismissed failure toast left the bell saying
// "Nothing needs you right now." Same store as the client bell, deliberately:
// a second store would be a second idea of what just happened here.
import { useToastStore, unreadToastCount, type ToastRecord } from '../../lib/toast-store';
import { pluralPhrase } from '../../lib/format/plural';
import { relativeTime } from '../../lib/format/date-time';
import { displayPersonName } from '../../lib/format/person-name';
import { displayLegalName } from '../../lib/format/legal-name';
import { initialsOf } from '../../lib/format/initials';
import { useSidebarScrollMemory } from '../../lib/use-sidebar-scroll-memory';

// ADMIN-07 (2026-06-09): the Cmd-K palette is a REAL command palette over a
// static set of in-app navigation commands derived from the same nav config
// the sidebar renders, so it never invents results or ships fixture data.
// The old firm-tenant trust quick-search rode on the retired /admin/clients
// list; a Client Book search (GET /v1/firm/clients) is the follow-up source
// to wire in without touching the palette shell.

// ── Nav item type ─────────────────────────────────────────────────────────────

type NavBadgeKey = 'queue' | 'master' | 'fica';

interface AdminNavItem {
  icon: IconName;
  label: string;
  key: string;
  to: string;
  /**
   * If set, the sidebar derives the badge count from the corresponding
   * query result via `useAdminQueueCounts()`. If the count is unknown
   * (loading / error / missing endpoint) the badge is omitted entirely
   * — no "?" placeholder, no '0' invented.
   */
  badgeKey?: NavBadgeKey;
  urgent?: boolean;
  /**
   * Role-gate (F12). When set, the item — in BOTH the sidebar and the Cmd-K
   * palette — is shown only to a current session role in this list (normalized
   * via route-policy `normalizeRole`, so the legacy `superadmin` spelling folds
   * into `super_admin`). Absent → visible to any firm-console role (the default
   * for Operate items). The current default matrix gates the Configure section
   * (Branding studio, Team, External auditors, AES sync log) to admin +
   * super_admin so a `compliance` / `rm` account no longer sees firm-config
   * surfaces it can't use. Paul-refineable — widen/narrow per item here.
   */
  roles?: Role[];
}

/** Is this nav item visible to `role`? Un-gated items (no `roles`) are visible
 *  to every firm-console role; gated items require membership. Shared by the
 *  sidebar and the Cmd-K palette so the two never drift. */
function canSeeNavItem(item: AdminNavItem, role: Role): boolean {
  return !item.roles || item.roles.includes(role);
}

// ── Nav config (v2) ───────────────────────────────────────────────────────────

// P2 #14 — `rm` is deliberately trust-scoped (ruling: do NOT widen backend
// access). GET /v1/firm/documents + /v1/firm/document-coverage are firm-wide
// aggregates that 403 for `rm` permanently; the rows below hid that backend
// boundary behind a nav link that led nowhere but a doomed request. Gate the
// nav entries to the roles the backend actually serves (everyone except rm)
// so the dead-end request never fires from the sidebar/palette either.
const NON_RM_STAFF: Role[] = ['admin', 'compliance', 'director', 'super_admin'];

// The firm's own FICA accountable-institution reporting register is a
// compliance-officer surface — backend gates it on isTenantAdminOrCompliance
// (admin/compliance/super_admin), so mirror that exact set here (director + rm
// 403 there today; see REASONING.md §6).
const COMPLIANCE_REPORT_ROLES: Role[] = ['admin', 'compliance', 'super_admin'];

// 2026-07-19 miss-hunt P1 (state-code-firm-01) — mirror the backend's two
// worklist tiers (lib/authz/firm-capabilities.ts ↔ backend tenant-roles.ts)
// so rm / director never get nav items that lead to guaranteed 403 pages:
//   - admin-or-compliance tier (dsar / master-lodgements / entity-graph):
//     rm EXCLUDED → those items reuse NON_RM_STAFF below.
//   - worklist tier (risk-review / BO attempts / EDD worker): director
//     EXCLUDED → the EDD item gets its own set.
const EDD_WORKER_ROLES: Role[] = ['admin', 'compliance', 'rm', 'super_admin'];

export const OPERATE_NAV: AdminNavItem[] = [
  { icon: 'overview', label: 'Dashboard',     key: 'dashboard',  to: '/admin/dashboard' },
  { icon: 'people',   label: 'Clients',       key: 'clientbook', to: '/admin/client-book' },
  { icon: 'audit',    label: 'My queue',      key: 'queue',     to: '/admin/queue',             badgeKey: 'queue', urgent: true },
  { icon: 'graph',    label: 'Entity graph',  key: 'entity-graph', to: '/admin/entity-graph',   roles: NON_RM_STAFF },
  { icon: 'master',   label: 'Master ICMS',   key: 'master',    to: '/admin/master-lodgements', badgeKey: 'master', roles: NON_RM_STAFF },
  { icon: 'shield',   label: 'FICA pipeline', key: 'fica',      to: '/admin/fica',              badgeKey: 'fica' },
  // doc-organization (G1/G2/G3/G4) — the firm-wide DOCUMENTS browser: every file
  // across the book, the Unfiled filter (orphans), filename search, assign-to-
  // trust. This is what an admin expects under "Documents" (find any file).
  // P2 #14: hidden from `rm` — the backend 403s that role permanently.
  { icon: 'doc',      label: 'Documents',     key: 'documents', to: '/admin/documents',         roles: NON_RM_STAFF },
  // P2-3 — firm-wide missing-documents COVERAGE dashboard (who's missing what
  // vs the core doc set). No badge (the missing count is the page's whole point).
  // P2 #14: hidden from `rm` — the backend 403s that role permanently.
  { icon: 'shield',   label: 'Doc coverage',  key: 'coverage',  to: '/admin/document-coverage',  roles: NON_RM_STAFF },
  // FICA s21 Enhanced Due Diligence (PEP / EDD) — nostalgic-hofstadter branch.
  // EDD-worker tier: director is excluded on the backend (EDD_WORKER_
  // AUTHORISED_ROLES) — hide the doomed link from that chair.
  { icon: 'shield',   label: 'PEP / EDD',     key: 'edd',       to: '/admin/edd',               roles: EDD_WORKER_ROLES },
  // The firm's OWN FICA reporting register (STR/CTR/TFS/TPR) — its
  // accountable-institution duties, distinct from any per-client FIC action.
  { icon: 'audit',    label: 'FICA reporting', key: 'compliance-reports', to: '/admin/compliance-reports', roles: COMPLIANCE_REPORT_ROLES },
];

// Configure = firm-config surfaces. Gated admin + super_admin (F12): a
// `compliance` / `rm` account has no business reconfiguring branding, team,
// auditor grants or the AES log, so these are hidden from the nav AND the Cmd-K
// palette for those roles (the backend remains the real authz boundary).
const CONFIGURE_ROLES: Role[] = ['admin', 'super_admin'];
// P3 #18 — billing-screen.tsx serves admin/compliance/super_admin (compliance
// is read-only there — canManageBilling gates the Subscribe/change-plan
// controls to admin+super_admin, but a compliance account can view the
// current plan and invoices). Mirror that same set here so the nav link
// matches what the screen actually allows in, instead of the narrower
// CONFIGURE_ROLES used by the firm-config-only items above.
const BILLING_ROLES: Role[] = ['admin', 'compliance', 'super_admin'];
const CONFIGURE_NAV: AdminNavItem[] = [
  { icon: 'brand',    label: 'Branding studio',   key: 'branding',  to: '/admin/branding',          roles: CONFIGURE_ROLES },
  { icon: 'team',     label: 'Team',              key: 'team',      to: '/admin/team',              roles: CONFIGURE_ROLES },
  // AUDITOR-03: the external-auditor grant screen was URL-only (reachable but
  // not in the nav). Surfaced under Configure alongside Team.
  { icon: 'external', label: 'External auditors', key: 'auditors',  to: '/admin/external-auditors', roles: CONFIGURE_ROLES },
  // W3 — AES (TrustFactory) synced-data verification log. Lets a compliance
  // admin verify each AES signing callback produced the right Elreign records.
  { icon: 'audit',    label: 'AES sync log',      key: 'aes',       to: '/admin/aes-sync-log',      roles: CONFIGURE_ROLES },
  // P3 #18 — Billing had no nav link anywhere (typed-URL only). Surfaced
  // under Configure alongside the other firm-admin surfaces.
  { icon: 'cash',     label: 'Billing',           key: 'billing',   to: '/admin/billing',           roles: BILLING_ROLES },
  // Partner programme earnings — the firm's referral commission view. Admin +
  // super_admin only (owner/money surface); the screen also degrades to an
  // honest "not a partner" note when the firm isn't enrolled.
  { icon: 'cash',     label: 'Partner earnings',  key: 'partner-earnings', to: '/admin/partner-earnings', roles: CONFIGURE_ROLES },
];

/**
 * Sidebar badge counts — derived from the admin queue queries the
 * dashboard already pulls. Each entry resolves to a number when the
 * source query has data, or `null` when the count is unknown (loading,
 * error, or no endpoint yet). The sidebar renders no badge for null
 * keys rather than inventing a "0" or "?".
 */
function useAdminQueueCounts(): Record<NavBadgeKey, number | null> {
  const { role } = useSession();
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';
  // Role-tier gating (state-code-firm-01): rm is outside the dsar tier,
  // director outside the risk-review tier — don't fire the doomed 403s from
  // every /admin navigation (same pattern as the recovery gate below).
  const canDsar = canUseAdminOrComplianceTier(role);
  const canWorklist = canUseWorklistTier(role);
  // Nothing the firm console reads is asked for while the operator stands in a
  // client's space (NEW-R12-MAPSTRUCTURE-ACTAS-401): the act-as token cannot
  // make the operator-only reads (401 at the gateway) and would make the rest
  // about the wrong tenant. The shell is mounted in that posture only in
  // passing, while a lazy /app route loads or behind the Client Book's Edit
  // drawer, and its cached answers come back the moment the posture does.
  const ownPosture = !useIsStandingIn();
  // The book-wide aggregate is the WHOLE-BOOK tier on the server
  // (`isTenantWideReader`), not the worklist tier. Gating it on the worklist
  // sent it for `rm`, which the server refuses (403 on every /admin page, found
  // by the persona kit 4 Oct 2026), and withheld it from `director`, which the
  // server allows. An `rm` falls back to its own scoped trusts read below.
  const canBook = canReadWholeBook(role) && ownPosture;

  const trustsQ = useTrusts({ enabled: ownPosture });
  // NEW-fica-overdue-two-sources (grader, 5 Sep 2026): the FICA figures come
  // from the one book-wide aggregate the dashboard tile reads, so the badge,
  // the bell and the tile cannot disagree. The own-tenant read stays as the
  // fallback for an environment without the aggregate, and for a chair that
  // cannot read the whole book.
  const bookQ = useFirmBookSummary({ enabled: canBook });
  const dsarQ = useAdminDsarList({ enabled: canDsar && ownPosture });
  const riskQ = useAdminRiskReview({ enabled: canWorklist && ownPosture });
  // Recovery-requests is super_admin-only on the backend. Gating the hook
  // (rather than always firing it and swallowing the 403) keeps the
  // browser console clean for tenant admins and avoids a wasted round-trip
  // per admin-page navigation.
  const recoveryQ = useAdminRecoveryRequests({ enabled: isSuperAdmin && ownPosture });

  // The "My queue" count mirrors the dashboard's queueRows pipeline —
  // it's the sum of pending DSARs, risk-review items, and (for super
  // admins) open recovery requests. Tiers the role can't access contribute
  // 0 (their queues don't exist for this chair — not an unknown).
  const recoveryContribution = isSuperAdmin
    ? (recoveryQ.data?.recoveryRequests.length ?? null)
    : 0;
  const dsarContribution = canDsar ? (dsarQ.data ? dsarQ.data.dsarRequests.length : null) : 0;
  const riskContribution = canWorklist ? (riskQ.data ? riskQ.data.items.length : null) : 0;
  const queueCount =
    dsarContribution !== null && riskContribution !== null && recoveryContribution !== null
      ? dsarContribution + riskContribution + recoveryContribution
      : null;
  // FICA pipeline = trusts with ficaDaysOut <= 30 (i.e. due or overdue), plus
  // the trusts whose INITIAL FICA is outstanding (TRS-03) — those carry no
  // clock at all, so counting them by day number would silently drop them.
  // Matches the dashboard's FICA deck filter (`buildFicaCards`).
  const ficaCount = bookQ.data
    ? bookQ.data.fica.overdue + bookQ.data.fica.dueIn30 + (bookQ.data.fica.unverified ?? 0)
    : trustsQ.data
      ? trustsQ.data.trusts.filter(
          (t) =>
            isInitialFicaOutstanding(t) ||
            (ficaDaysOutOf(t) ?? Number.POSITIVE_INFINITY) <= 30,
        ).length
      : null;

  return {
    queue: queueCount,
    // Master ICMS lodgement counts are owned by useMasterLodgements (not
    // yet wired) — leave as null so the sidebar omits the badge.
    master: null,
    fica: ficaCount,
  };
}

// ── Notifications ─────────────────────────────────────────────────────────────

/** One line in the bell popover: a real count, and the surface that owns it. */
interface AttentionRow {
  key: string;
  label: string;
  count: number;
  to: string;
  urgent?: boolean;
}

/**
 * What actually needs this operator right now.
 *
 * Sourced from the SAME three queries the sidebar badge and the dashboard queue
 * already run (react-query dedupes them by key, so the bell costs no extra
 * round-trip), plus the FICA-overdue slice of the trusts read. That is the
 * whole point: the bell must never become a fourth place with its own idea of
 * how much work is outstanding — the number under the bell and the number on
 * "My queue" are the same number by construction.
 *
 * A tier the role cannot reach contributes nothing rather than an error: `rm`
 * is outside the DSAR tier and `director` outside risk-review, and their queues
 * genuinely do not exist for that chair.
 */
function useAdminAttentionRows(): { rows: AttentionRow[]; loading: boolean } {
  const { role } = useSession();
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';
  const canDsar = canUseAdminOrComplianceTier(role);
  const canWorklist = canUseWorklistTier(role);
  // Same posture rule as the badge counts above.
  const ownPosture = !useIsStandingIn();
  const canBook = canReadWholeBook(role) && ownPosture;

  const trustsQ = useTrusts({ enabled: ownPosture });
  // One source for "FICA overdue" (see the badge counts above), gated on the
  // server's own tier for it.
  const bookQ = useFirmBookSummary({ enabled: canBook });
  const dsarQ = useAdminDsarList({ enabled: canDsar && ownPosture });
  const riskQ = useAdminRiskReview({ enabled: canWorklist && ownPosture });
  const recoveryQ = useAdminRecoveryRequests({ enabled: isSuperAdmin && ownPosture });

  const rows: AttentionRow[] = [];
  const dsarCount = canDsar ? (dsarQ.data?.dsarRequests.length ?? 0) : 0;
  if (dsarCount > 0) {
    rows.push({
      key: 'dsar',
      label: `${dsarCount} data request${dsarCount === 1 ? '' : 's'} awaiting a response`,
      count: dsarCount,
      to: '/admin/dsar',
      urgent: true,
    });
  }
  const riskCount = canWorklist ? (riskQ.data?.items.length ?? 0) : 0;
  if (riskCount > 0) {
    // QUE-02 (M3 B5, Paul): "I think it would be great if you just could say
    // more detail. Lubabalo Trust needs a risk review, not one client in risk
    // review."
    //
    // Two things were wrong with the old line and only one of them was the
    // count. It said CLIENT for a queue of TRUSTS — a client may hold several,
    // and the queue row beside it has always named the trust — and it named
    // nothing at all, so the bell could tell you the size of the problem and
    // never what the problem was. The queue row already carries `trustName`;
    // this reads the same field, so the bell and the row cannot disagree.
    const firstNamed = displayLegalName(riskQ.data?.items.find((i) => i.trustName?.trim())?.trustName?.trim()) || undefined;
    const others = riskCount - 1;
    rows.push({
      key: 'risk',
      label: !firstNamed
        ? `${riskCount} trust${riskCount === 1 ? '' : 's'} need${riskCount === 1 ? 's' : ''} a risk review`
        : others === 0
          ? `${firstNamed} needs a risk review`
          : `${firstNamed} and ${others} other trust${others === 1 ? '' : 's'} need a risk review`,
      count: riskCount,
      to: '/admin/risk-review',
    });
  }
  const recoveryCount = isSuperAdmin ? (recoveryQ.data?.recoveryRequests.length ?? 0) : 0;
  if (recoveryCount > 0) {
    rows.push({
      key: 'recovery',
      label: `${recoveryCount} account recovery request${recoveryCount === 1 ? '' : 's'}`,
      count: recoveryCount,
      to: '/admin/recovery-requests',
      urgent: true,
    });
  }
  // FICA reviews already past their date — the one item on this list that is a
  // deadline rather than an inbox, so it is worth naming separately from the
  // "due soon" figure the sidebar badge carries.
  // TRS-03: a trust with no initial FICA is NOT an overdue review — it has no
  // review cycle to be late for. It is counted in the pipeline badge above, and
  // a missing clock never becomes an overdue one here.
  const ficaOverdue = bookQ.data
    ? bookQ.data.fica.overdue
    : trustsQ.data
      ? trustsQ.data.trusts.filter((t) => (ficaDaysOutOf(t) ?? Number.POSITIVE_INFINITY) <= 0)
          .length
      : 0;
  if (ficaOverdue > 0) {
    rows.push({
      key: 'fica',
      label: `${ficaOverdue} FICA review${ficaOverdue === 1 ? '' : 's'} overdue`,
      count: ficaOverdue,
      to: '/admin/fica',
      urgent: true,
    });
  }

  // EVERY query that feeds a row has to be in here, or the bell publishes a
  // subtotal as a total. `bookQ` was missing, and it is the one the FICA-overdue
  // row reads: the count went 1 then 11 on a cold /admin/dashboard because the
  // fast queries answered first and the bell called that the answer (grader
  // round 5, 8 September 2026). A query this chair cannot run contributes
  // nothing, so it is gated the same way its row is.
  const loading =
    trustsQ.isLoading ||
    (canBook && bookQ.isLoading) ||
    (canDsar && dsarQ.isLoading) ||
    (canWorklist && riskQ.isLoading) ||
    (isSuperAdmin && recoveryQ.isLoading);

  return { rows, loading };
}

/**
 * What just happened in this browser.
 *
 * Paul, 25 August: "those [toasts] that appear at the bottom right, it should
 * be on the notification bell, and then I should be able to click on it and
 * view all of the notifications or dismiss it. Otherwise this bell is
 * unnecessary." That was built for the client shell and never for this one, so
 * the firm console, where invites are sent and where they fail, kept losing the
 * only trace of a failed send the moment its toast was dismissed.
 *
 * Under its own heading rather than merged into "Needs you" above. Those are
 * two different kinds of fact: the queue is work this firm owes, recorded on the
 * server; this is what happened in this tab. Failures lead, because a failure is
 * what a reader came back for.
 */
function AdminToastHistory({
  records,
  onDismiss,
}: {
  records: ReadonlyArray<ToastRecord>;
  onDismiss: (id: string) => void;
}) {
  if (records.length === 0) return null;
  const failed = (k: ToastRecord['kind']): boolean => k === 'error' || k === 'warn';
  const ordered = [...records].sort((a, b) => Number(failed(b.kind)) - Number(failed(a.kind)));

  return (
    <div data-testid="notif-toast-history" style={{ marginTop: 2 }}>
      <div
        className="nav-section-label"
        style={{
          padding: '10px 10px 6px',
          marginTop: 4,
          borderTop: '1px solid var(--hairline)',
        }}
      >
        On this device
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ordered.map((r) => (
          <li
            key={r.id}
            data-testid={`notif-toast-${r.id}`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 9,
              border: '1px solid var(--hairline)',
              background: failed(r.kind) ? 'var(--negative-soft)' : 'var(--bg)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, lineHeight: 1.5, color: 'var(--ink)' }}>
                {r.message}
              </span>
              <span style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                  {relativeTime(new Date(r.at).toISOString())}
                </span>
                {r.ref && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-4)' }}>
                    {r.ref}
                  </span>
                )}
              </span>
            </span>
            <button
              type="button"
              data-testid={`notif-toast-dismiss-${r.id}`}
              onClick={() => onDismiss(r.id)}
              aria-label="Dismiss this message"
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--ink-3)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Dismiss
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The notification bell.
 *
 * It used to be a `<button>` with an aria-label and no `onClick` whatsoever —
 * clicking it did nothing at all, in front of the team (2026-08-23). The old
 * comment on it explained that the always-on unread DOT had been removed for
 * having no backing count, and stopped there, leaving a control that looks
 * interactive and is not.
 *
 * A dead click is worse than an empty popover, so this always opens something:
 * either the real outstanding work, or an honest, calm statement that there is
 * none. It does NOT invent a notification feed — there is no such endpoint, and
 * a fabricated one on a compliance surface is exactly the failure mode the
 * fixture purge existed to end.
 */
function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { rows, loading } = useAdminAttentionRows();
  const total = rows.reduce((n, r) => n + r.count, 0);
  const hasUrgent = rows.some((r) => r.urgent);

  // The same store the client bell reads. Nothing here is a server fact, which
  // is why it renders under its own heading below and is counted separately.
  const history = useToastStore((t) => t.history);
  const dismissHistory = useToastStore((t) => t.dismissHistory);
  const markHistorySeen = useToastStore((t) => t.markHistorySeen);
  const unreadLocal = unreadToastCount(history);
  const localFailure = history.some((h) => h.kind === 'error' || h.kind === 'warn');

  // Opening the bell is reading it. Marking on open rather than on render keeps
  // the badge meaningful for someone who never opens it.
  useEffect(() => {
    if (open) markHistorySeen();
  }, [open, markHistorySeen]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="icon-btn"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          // ── DESIGN GATE A2, BOTH HALVES ───────────────────────────────────
          // Round 2: on a cold API this said "nothing outstanding" for about
          // ten seconds before the queries settled to ten items. An all-clear
          // the app has not yet checked is a false empty.
          // Round 5: the SAME fault the other way up. It read "1 item need you"
          // for several seconds and then settled at 11, because the count was
          // published the moment the FIRST of five queries answered. A partial
          // aggregate announced as a total is a wrong number, not an early one,
          // and on a compliance surface an operator who reads "1" and stops is
          // the whole cost. So `loading` is now tested FIRST: while any of the
          // reads is still in flight the bell says it is still reading, exactly
          // as the tiles below it do, whatever the running subtotal happens to
          // be.
          loading
            ? 'Notifications, still reading'
            : total > 0
              ? `Notifications, ${pluralPhrase(total, '{n} item needs you', '{n} items need you')}`
              : unreadLocal > 0
                ? `Notifications, ${pluralPhrase(
                    unreadLocal,
                    '{n} recent message on this device',
                    '{n} recent messages on this device',
                  )}`
                : 'Notifications, nothing outstanding'
        }
        data-testid="notifications-bell"
        onClick={() => setOpen((o) => !o)}
        /* `.icon-btn` already carries the 34px `--icon-btn` box and
           `position: relative` (for the dot). Cohesion pass phase 3: the inline
           copy is gone so the console bell and the portal bell are one size by
           construction rather than by two numbers agreeing. */
      >
        <Icon name="bell" size={16} />
        {/* The dot is back, but ONLY behind a real count — which is what the
            2026-05-25 audit actually objected to when it removed the
            always-on version. */}
        {(total > 0 || unreadLocal > 0) && (
          <span
            aria-hidden="true"
            data-testid="notifications-dot"
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 7,
              height: 7,
              borderRadius: '50%',
              // A failed send that nobody has read yet is the reason this dot
              // needs to be red, not the polite accent.
              background:
                hasUrgent || (unreadLocal > 0 && localFailure)
                  ? 'var(--negative)'
                  : 'var(--chip-accent)',
            }}
          />
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          data-testid="notifications-popover"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 320,
            padding: 6,
            // The local list holds up to 30 entries, so the popover has to be
            // able to run out of room without running off the screen.
            maxHeight: 'min(70vh, 520px)',
            overflowY: 'auto',
            background: 'var(--bg)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(20, 18, 12, 0.10), 0 2px 4px rgba(20, 18, 12, 0.04)',
            zIndex: 60,
          }}
        >
          {/* The heading only earns its place when the section under it has
              something to say. With no outstanding work but a failure recorded
              in this tab, "Nothing needs you right now" is true of the queue and
              a lie about the thing the reader opened the bell to find. */}
          {(loading || rows.length > 0 || history.length === 0) && (
            <div
              className="nav-section-label"
              style={{ padding: '8px 10px 6px' }}
            >
              Needs you
            </div>
          )}
          {loading ? (
            <div style={{ padding: '14px 10px', fontSize: 13, color: 'var(--ink-3)' }} aria-live="polite">
              Checking&hellip;
            </div>
          ) : rows.length === 0 ? (
            history.length > 0 ? null : (
            <div
              data-testid="notifications-empty"
              style={{
                padding: '18px 12px 20px',
                fontSize: 13.5,
                color: 'var(--ink-2)',
                lineHeight: 1.55,
              }}
            >
              Nothing needs you right now.
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-3)' }}>
                Outstanding data requests, risk reviews and overdue FICA reviews
                appear here.
              </div>
            </div>
            )
          ) : (
            rows.map((r) => (
              <Link
                key={r.key}
                to={r.to as never}
                data-testid={`notification-${r.key}`}
                onClick={() => setOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 11px',
                  borderRadius: 9,
                  fontSize: 13,
                  color: 'var(--ink)',
                  textDecoration: 'none',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: r.urgent ? 'var(--negative)' : 'var(--chip-accent)',
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
              </Link>
            ))
          )}
          <AdminToastHistory records={history} onDismiss={dismissHistory} />
        </div>
      )}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export function AdminSidebar({
  /** Drawer state at phone width (cohesion pass phase 3). Undefined on desktop
   *  callers, where the rail is a grid column and neither prop does anything. */
  open,
  onNavigate,
}: {
  open?: boolean;
  onNavigate?: () => void;
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  // NAV-02 (2026-08-27). Paul, 25 August: "the screen resets to the top here on
  // the menu side on the left... this menu side is its own scroll bar. So the
  // menu shouldn't be reset to the top, otherwise it feels awkward."
  //
  // A 26 August pass fixed the CLIENT sidebar and stopped there. It was easy to
  // believe this console was covered, because `routes/admin/_layout.tsx` looks
  // like a persistent layout. It is not: it returns a bare <Outlet/>, and every
  // one of the twenty-odd admin screens mounts its own <AdminLayout>. So this
  // sidebar unmounts on every single navigation, exactly as the client one did,
  // and `.nav.admin` carries its own `overflow-y: auto`. This is the longest
  // sidebar in the product, so it is the one the reset is most obvious on.
  const sidebarScroll = useSidebarScrollMemory<HTMLElement>('admin-sidebar');

  // Account menu on the profile foot (sign-out lives here — 2026-08-22).
  const [footOpen, setFootOpen] = useState(false);
  const footRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!footOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!footRef.current?.contains(e.target as Node)) setFootOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFootOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [footOpen]);
  // F12: role-gate the nav. Source the role from the same place route-policy
  // does (the session), normalized so the legacy `superadmin` spelling folds in.
  const { role: rawRole } = useSession();
  const role = normalizeRole(rawRole);
  const operateNav = OPERATE_NAV.filter((it) => canSeeNavItem(it, role));
  const configureNav = CONFIGURE_NAV.filter((it) => canSeeNavItem(it, role));
  // F7: the profile foot + tenant chip identify the logged-in OPERATOR, never
  // the act-as client. The operator-pinned hooks force useOperatorSession and
  // live under isolated query keys, so they never show the stale client
  // identity (the "Signed-in user → client email → firm email" flash) that the
  // act-as-following useMe()/useTenantMe() produced when landing from a portal.
  const tenantQ = useOperatorTenantMe();
  // While standing in a client's space this read can only 401 (an act-as token
  // on an operator-only route), so it waits for the operator's own posture.
  const standingIn = useIsStandingIn();
  // GET /v1/admin/branding is the admin-or-compliance tier on the server. For
  // any other chair (an `rm`) the firm name and portal host come from the
  // tenant record and the host's public branding below, which every staff role
  // can read; asking anyway was a 403 on every /admin page.
  const brandingQ = useBranding({
    enabled: canReadFirmSettings(rawRole) && !standingIn,
  });
  const activeBranding = useActiveBranding();
  const meQ = useOperatorMe();
  const badgeCounts = useAdminQueueCounts();
  const meUser = meQ.data?.user;
  const meDisplayName = meUser?.displayName ?? meUser?.email ?? '';
  // 2026-08-22 (Klaprops meeting): this rendered the RAW SLUG — a director saw
  // "super admin" / "rm" under their own name in the identity foot, the only
  // place in the console that still spoke database. It now reads the shared
  // label map, overlaid with the firm's own role titles from the OPERATOR's
  // tenant (never the act-as client's — the whole point of this foot is that it
  // identifies the logged-in human).
  const meRoleLabel = meUser?.role
    ? labelForRole(meUser.role, tenantQ.data?.roleLabels)
    : '';
  // The one initials rule; '··' only while there is no name at all.
  const meInitials = meDisplayName ? initialsOf(meDisplayName) : '··';

  // Priority: published firmName > tenant displayName > Elreign default placeholder.
  const tenantName =
    brandingQ.data?.branding.firmName ??
    tenantQ.data?.display_name ??
    'Elreign';
  // Slug is now surfaced on /v1/tenants/me; fall back to the tenant id only if
  // the doc is missing. Rendered as the PORTAL HOST rather than the bare slug:
  // under a one-word firm name the bare slug is literally the same word again
  // ("Destinata" over "destinata"), which is what Paul hit on 2026-08-11.
  const tenantSlug = tenantQ.data?.slug ?? tenantQ.data?.tenant_id ?? '';
  const tenantPortalHost = portalHostForTenant(
    tenantQ.data?.slug ?? null,
    brandingQ.data?.branding.customDomain ?? null,
  );

  // firm-walkthrough 2026-07-20 (P2): show the firm name ONCE in the lockup. A
  // logo carries identity → no wordmark beside it (the tenant chip names the
  // firm); with no logo the wordmark shows and the chip name is suppressed only
  // when it would exactly repeat it.
  const lockup = resolveBrandLockup({
    hasLogo: !!activeBranding?.logoUrl,
    brandingFirmName: activeBranding?.firmName,
    tenantName,
  });

  // BRD-05 (Paul, M1 §H): a firm with no logo shows the FIRM'S NAME, never the
  // Elreign "E". This console is a firm's own surface, so the tenant's display
  // name IS a firm name even before anything is published — a director on day
  // one sees their practice's initials, not Elreign's letterform. The Elreign
  // mark is kept for the one case with no name at all (an unresolved account),
  // and the `'Elreign'` placeholder `tenantName` falls back to is deliberately
  // NOT in this chain: a placeholder is not a name.
  const firmMarkName = (
    activeBranding?.firmName ??
    brandingQ.data?.branding.firmName ??
    tenantQ.data?.display_name ??
    ''
  ).trim();
  const noLogoMark = firmMarkName ? <FirmMonogram name={firmMarkName} size={24} /> : <ReignMark size={24} />;

  function isActive(item: AdminNavItem): boolean {
    if (item.to === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    return location.pathname === item.to || location.pathname.startsWith(item.to + '/');
  }

  return (
    /* `role="navigation"` (2026-08-22 a11y sweep): an <aside> is a
       COMPLEMENTARY landmark, so the admin console's primary navigation was
       announced as "complementary" and the console had no navigation landmark
       at all. The explicit role overrides the implicit one and `aria-label`
       names it. Still an <aside>, so `.nav.admin` styling is unchanged. */
    <aside
      ref={sidebarScroll.ref}
      onScroll={sidebarScroll.onScroll}
      className={'nav admin' + (open ? ' open' : '')}
      role="navigation"
      aria-label="Primary"
      /* THE INLINE 228 IS GONE (cohesion pass phase 3, 2026-09-15). Phase 1
         reconciled the rail to one token, `--rail-w: 240px`, read by the grid
         track in `.app.admin` — and this inline width quietly beat it, so the
         console rail sat 240px wide in the grid with a 228px aside inside it
         and the phase-1 sweep recorded a change that had not happened. It also
         made the phone drawer impossible: an inline width outranks the 264px
         the drawer rule sets. */
    >
      {/* Brand + tenant chip */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="brand-row" style={{ alignItems: 'center' }}>
          {activeBranding?.logoUrl ? (
            <FirmLogo
              src={activeBranding.logoUrl}
              alt={activeBranding.firmName ? `${activeBranding.firmName} logo` : 'Firm logo'}
              // A broken logo URL degrades to the same no-logo answer (the
              // firm's monogram), never to a broken-image glyph or the "E".
              fallback={noLogoMark}
              style={{ height: 30, width: 'auto', maxWidth: 168, objectFit: 'contain', display: 'block' }}
            />
          ) : (
            noLogoMark
          )}
          {lockup.showWordmark && <div className="wordmark">{lockup.wordmark}</div>}
        </div>
        <div className="tenant-chip">
          {lockup.showTenantName && <div className="tenant-name">{tenantName}</div>}
          <div className="tenant-slug" title={tenantSlug}>{tenantPortalHost}</div>
        </div>
      </div>

      {/* Operate */}
      <div>
        <div className="nav-section-label">Operate</div>
        <div className="nav-list">
          {operateNav.map((it) => {
            const count = it.badgeKey ? badgeCounts[it.badgeKey] : null;
            return (
              <Link
                key={it.key}
                to={it.to}
                className={'nav-item' + (isActive(it) ? ' active' : '')}
                aria-current={isActive(it) ? 'page' : undefined}
                onClick={onNavigate}
              >
                <Icon name={it.icon} size={16} />
                <span>{it.label}</span>
                {/* 2026-08-25 visual sweep: a "0" badge is noise — the badge
                    exists to pull the eye at a non-zero count, so zero renders
                    nothing rather than a permanent grey chip of reassurance. */}
                {count != null && count > 0 && (
                  <span className={'nav-badge' + (it.urgent ? ' urgent' : '')}>
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Configure — hidden entirely when the role can see none of its items
          (e.g. a compliance / rm account), so no empty-section header shows. */}
      {configureNav.length > 0 && (
        <div>
          <div className="nav-section-label">Configure</div>
          <div className="nav-list">
            {configureNav.map((it) => (
              <Link
                key={it.key}
                to={it.to}
                className={'nav-item' + (isActive(it) ? ' active' : '')}
                aria-current={isActive(it) ? 'page' : undefined}
                onClick={onNavigate}
              >
                <Icon name={it.icon} size={16} />
                <span>{it.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Profile foot — a MENU, not a label. The firm console shipped with no
          sign-out control at all (found live, 2026-08-22 Klaprops walkthrough:
          "you can't even sign out"); this mirrors ScreenShell's AccountMenu. */}
      <div className="nav-foot" ref={footRef} style={{ position: 'relative' }}>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={footOpen}
          aria-label="Open account menu"
          onClick={() => setFootOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', minWidth: 0,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', textAlign: 'left', font: 'inherit', padding: 0,
          }}
        >
          <div
            className="avatar"
            style={{ width: 32, height: 32, fontSize: 12 }}
            aria-hidden="true"
          >
            {meInitials}
          </div>
          <div className="nav-foot-name" style={{ minWidth: 0, flex: 1 }}>
            {meQ.isLoading ? (
              <span
                aria-busy="true"
                style={{
                  display: 'inline-block',
                  width: 90,
                  height: 12,
                  background: 'var(--hairline)',
                  borderRadius: 4,
                }}
              />
            ) : (
              <>
                {/* NAV-03 + TYP-02. The name wraps to two lines rather than
                    clipping (the rule lives on `.nav-foot-name b`, shared with
                    the other two shells), and it is cased at render by the one
                    shared rule so an all-capitals value off an identity document
                    stops shouting here while reading in ordinary case elsewhere.
                    The stored string stays on `title`.

                    The <br/> that used to sit between these two is gone: both
                    are block-level now that the wrap rule applies, so it added a
                    blank line rather than a line break. */}
                <b style={{ fontSize: 12.5 }} title={meDisplayName || undefined}>
                  {displayPersonName(meDisplayName) || 'Signed-in user'}
                </b>
                {/* No `text-transform: capitalize` any more — it existed to
                    make a raw slug presentable, and would now Title-Case a real
                    title ("Compliance Officer", "Client Partner"). */}
                <span style={{ fontSize: 11 }}>
                  {meRoleLabel || 'Member'}
                </span>
              </>
            )}
          </div>
          <svg
            width={14}
            height={14}
            viewBox="0 0 20 20"
            aria-hidden="true"
            style={{
              flexShrink: 0,
              color: 'var(--ink-3)',
              transform: footOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 160ms var(--ease-cushion)',
            }}
          >
            <path
              d="M5 8l5 5 5-5"
              stroke="currentColor"
              fill="none"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {footOpen && (
          <div
            role="menu"
            aria-label="Account"
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              minWidth: 200,
              padding: 6,
              background: 'var(--bg)',
              border: '1px solid var(--hairline-2)',
              borderRadius: 12,
              boxShadow:
                '0 12px 32px rgba(20, 18, 12, 0.08), 0 2px 4px rgba(20, 18, 12, 0.04)',
              zIndex: 50,
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setFootOpen(false);
                void signOutAndRedirect(navigate);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                padding: '9px 11px',
                borderRadius: 9,
                border: 'none',
                background: 'transparent',
                color: 'var(--ink)',
                fontFamily: 'var(--sans)',
                fontSize: 13.5,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Icon name="external" size={16} />
              <span>Sign out</span>
            </button>
          </div>
        )}
      </div>

      {/* Internal build badge — P3 #26: was crowding the branded firm-name
          lockup at the top (forcing an awkward wrap around the mark). Lives
          here, muted, below the profile foot — visible for support/debug
          purposes without competing with the firm's own branding. */}
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-4)',
          letterSpacing: '0.04em',
          textAlign: 'center',
        }}
      >
        v2.4.1
      </div>
    </aside>
  );
}

// ── Cmd-K palette ─────────────────────────────────────────────────────────────

interface CmdKPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * DSH-07 — the control that opened the palette. The panel drops from it.
   *
   * M3 C3, Paul on the deployed build: "with the search button, can you rather
   * put this jump to screen closer to the search bar where I tap search." He
   * clicked Search in the top right and the panel appeared floating in the
   * middle of the viewport, roughly 600px away from the thing he had just
   * pressed and with no visual line back to it, so it read as a separate screen
   * rather than as that button opening.
   *
   * Optional on purpose: without an anchor the panel keeps the centred
   * stylesheet position (`.cmdk-panel` in design-system.css), which is right for
   * the ⌘K shortcut and for callers that render the palette with no trigger.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Where the panel sits when it was opened by a button rather than by ⌘K.
 *
 * Returns inline overrides for the stylesheet's centred `left/top/transform`;
 * `null` means "leave the CSS alone". Right-aligned to the trigger, because the
 * trigger lives at the right end of the topbar and a left-aligned drop would run
 * off the screen. `Math.max(…, MIN_GUTTER)` keeps the panel on-screen when the
 * viewport is narrower than the panel itself.
 */
const CMDK_GUTTER = 16;
export function cmdkAnchorStyle(
  rect: { top: number; bottom: number; right: number } | null,
  viewportWidth: number,
): React.CSSProperties | null {
  if (!rect) return null;
  return {
    left: 'auto',
    transform: 'none',
    top: Math.round(rect.bottom + 8),
    right: Math.round(Math.max(CMDK_GUTTER, viewportWidth - rect.right)),
    // The centred rule caps height against the viewport; dropping from the
    // topbar leaves less room below, so cap against what is actually there.
    maxHeight: `calc(100vh - ${Math.round(rect.bottom + 8 + CMDK_GUTTER)}px)`,
  };
}

/** A single selectable row in the palette. */
interface CmdKItem {
  /** Stable React key + a11y id. */
  id: string;
  /** Primary label shown in bold. */
  label: string;
  /** Secondary muted line (e.g. a section name). */
  sub?: string;
  /** Tiny leading glyph. */
  icon: IconName;
  /** Group heading this item sits under. */
  group: 'Go to';
  /** Navigation target — TanStack `to`. */
  to: string;
  /** Lowercased haystack the query is matched against. */
  haystack: string;
}

/** Quick-navigation commands — derived from the SAME nav config the sidebar
 *  renders, so the palette never drifts from the real routes. Role-gated (F12)
 *  through the SAME `canSeeNavItem` predicate the sidebar uses, so a Configure
 *  surface a role can't see never appears as a Cmd-K jump target either. */
function buildNavCommands(role: Role): CmdKItem[] {
  return [...OPERATE_NAV, ...CONFIGURE_NAV]
    .filter((it) => canSeeNavItem(it, role))
    .map((it) => ({
      id: `nav:${it.key}`,
      label: it.label,
      sub: 'Go to',
      icon: it.icon,
      group: 'Go to' as const,
      to: it.to,
      haystack: it.label.toLowerCase(),
    }));
}

export function CmdKPalette({ open, onClose, anchorRef }: CmdKPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  // DSH-07 — measured, not guessed. The trigger's x moves with the sidebar
  // width, the tenant switcher's label and whatever `topbarRight` a screen
  // passes, so a hardcoded offset would be right on the dashboard and wrong on
  // the six screens that compose extra controls into the same row.
  const [anchorStyle, setAnchorStyle] = useState<React.CSSProperties | null>(null);

  // F12: the palette lists only the commands the current role may reach.
  const { role: rawRole } = useSession();
  const role = normalizeRole(rawRole);
  const navCommands = useMemo(() => buildNavCommands(role), [role]);

  // Filter the nav commands by the query. With no query we still show them
  // all — a useful command launcher.
  const results = useMemo<CmdKItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navCommands;
    return navCommands.filter((c) => c.haystack.includes(q));
  }, [query, navCommands]);

  const runItem = useCallback(
    (item: CmdKItem | undefined) => {
      if (!item) return;
      onClose();
      void navigate({ to: item.to } as never);
    },
    [navigate, onClose],
  );

  // Reset query + selection when opened.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
    }
  }, [open]);

  // DSH-07 — measure the trigger on open, and again if the window resizes while
  // the panel is up (the topbar reflows, so a stale rect would leave the panel
  // pointing at nothing). Cleared on close so a later ⌘K open with no anchor
  // does not inherit the last button's position.
  useEffect(() => {
    if (!open) {
      setAnchorStyle(null);
      return;
    }
    const measure = () => {
      const el = anchorRef?.current;
      if (!el || typeof el.getBoundingClientRect !== 'function') {
        setAnchorStyle(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // jsdom reports an all-zero rect for every element; treat that as "no
      // usable anchor" rather than pinning the panel to the top-left corner.
      if (r.width === 0 && r.height === 0) {
        setAnchorStyle(null);
        return;
      }
      setAnchorStyle(cmdkAnchorStyle(r, window.innerWidth));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, anchorRef]);

  // Clamp the active index whenever the result set shrinks.
  useEffect(() => {
    setActiveIdx((i) => (i >= results.length ? Math.max(0, results.length - 1) : i));
  }, [results.length]);

  // Keyboard: ESC closes, ↑/↓ move, ⏎ opens the active row.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        runItem(results[activeIdx]);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, results, activeIdx, runItem]);

  // Keep the active row scrolled into view as the user arrows through.
  // Guarded: scrollIntoView is absent in some test/SSR DOMs (e.g. jsdom).
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmdk-idx="${activeIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  if (!open) return null;

  // Group rows for headed rendering while keeping a single flat index space
  // (so keyboard nav + the active highlight line up across groups).
  let runningIdx = -1;
  const groups: { name: string; items: { item: CmdKItem; idx: number }[] }[] = [];
  for (const item of results) {
    runningIdx += 1;
    const last = groups[groups.length - 1];
    if (last && last.name === item.group) {
      last.items.push({ item, idx: runningIdx });
    } else {
      groups.push({ name: item.group, items: [{ item, idx: runningIdx }] });
    }
  }

  return (
    <div
      className="admin-v2"
      style={{ position: 'fixed', inset: 0, zIndex: 9000 }}
      onClick={onClose}
    >
      <div className="cmdk-overlay" />
      <div
        className="cmdk-panel"
        data-testid="cmdk-panel"
        data-anchored={anchorStyle ? 'true' : 'false'}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Command palette"
        {...(anchorStyle ? { style: anchorStyle } : {})}
      >
        {/* Search input */}
        <div className="cmdk-input-row">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--ink-4)', flexShrink: 0 }}>
            <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.4" />
            <path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            autoFocus
            type="text"
            className="cmdk-input"
            placeholder="Jump to a screen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-results"
            aria-activedescendant={results[activeIdx]?.id}
          />
          <kbd className="cmdk-esc-key">ESC</kbd>
        </div>

        {/* Results — real, derived from the nav config. */}
        <div className="cmdk-results" id="cmdk-results" role="listbox" ref={listRef}>
          {results.length === 0 ? (
            <div
              data-testid="cmdk-empty"
              style={{
                padding: '24px 18px',
                fontSize: 13,
                color: 'var(--ink-3)',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              {`No matches for “${query.trim()}”.`}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.name} role="group" aria-label={g.name}>
                <div
                  className="nav-section-label"
                  style={{ padding: '8px 16px 4px' }}
                >
                  {g.name}
                </div>
                {g.items.map(({ item, idx }) => {
                  const isActive = idx === activeIdx;
                  return (
                    <button
                      key={item.id}
                      id={item.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-cmdk-idx={idx}
                      data-testid="cmdk-result"
                      onClick={() => runItem(item)}
                      onMouseMove={() => setActiveIdx(idx)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 16px',
                        border: 'none',
                        background: isActive ? 'var(--surface-2)' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'var(--sans)',
                      }}
                    >
                      <Icon name={item.icon} size={15} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'var(--ink)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.label}
                        </span>
                        {item.sub && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: 11.5,
                              color: 'var(--ink-3)',
                            }}
                          >
                            {item.sub}
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)' }}>
                          ⏎
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="cmdk-foot">
          <span aria-hidden="true">
            {results.length} result{results.length === 1 ? '' : 's'}
          </span>
          <span>↑↓ navigate · ⏎ open · ESC close</span>
        </div>
      </div>
    </div>
  );
}

// ── Layout shell ──────────────────────────────────────────────────────────────

interface AdminLayoutProps {
  eyebrow: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  topbarRight?: ReactNode;
}

/**
 * Returns true when the event target is an editable field — used by the
 * ⌘K listener to back off when the user is typing in an input/textarea
 * (so Cmd-K in an address field is still a browser shortcut, not a
 * palette hijack).
 *
 * Exported so it can be exercised in isolation by unit tests (jsdom's
 * focus behaviour for contentEditable is unreliable, so we test the
 * helper rather than threading it through dispatchEvent).
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.getAttribute('contenteditable') === 'true') return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function AdminLayout({ eyebrow, title, sub, children, topbarRight }: AdminLayoutProps) {
  // Feed the tenant store so <TenantSwitcher/> below has something to render.
  // ScreenShell has done this since 2026-08-06; /admin never renders ScreenShell,
  // which is why the switcher was invisible on the firm's own landing surface.
  useSyncMyTenants();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  // THE DRAWER (cohesion pass phase 3, 2026-09-15). Same state, same two
  // elements and the same stylesheet rules the client portal has had since the
  // Henry sweep. The console kept a fixed rail at every width with nothing to
  // fall back to, so all twenty-seven console routes scrolled sideways at
  // 375px. Below 860px the rail is now an off-canvas drawer; above it, `navOpen`
  // is inert because the rail is a grid column and the hamburger is display:none.
  const [navOpen, setNavOpen] = useState(false);
  // DSH-07 — the Search button the palette drops from. Null while the shell
  // renders under ⌘K only; the palette falls back to its centred position.
  const cmdkTriggerRef = useRef<HTMLButtonElement | null>(null);
  const openCmdk = useCallback(() => setCmdkOpen(true), []);
  const closeCmdk = useCallback(() => setCmdkOpen(false), []);
  // Theme is owned by the shell, not by a screen: it writes `data-theme` on
  // <html> so portals and modals rendered outside this subtree re-skin too.
  const { theme, toggleTheme } = useAdminTheme();

  // ⌘K keyboard shortcut.
  //
  // Guard: skip when the active target is an editable field — otherwise the
  // ⌘K in a search box (or any focused <input>) is hijacked. We check both
  // `e.target` AND `document.activeElement` because the event target may be a
  // parent container in some focus cases. The palette itself (CmdKPalette)
  // owns its own ESC / ↑↓ / ⏎ handling once open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'k')) return;
      if (isEditableTarget(e.target) || isEditableTarget(document.activeElement)) {
        return;
      }
      e.preventDefault();
      setCmdkOpen((v) => !v);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <CmdKPalette open={cmdkOpen} onClose={closeCmdk} anchorRef={cmdkTriggerRef} />
      <div className="app admin" style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <SkipToContentLink />
        <AdminSidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
        <div
          className={'nav-backdrop' + (navOpen ? ' open' : '')}
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
        <main className="admin" id="main" tabIndex={-1}>
          {/* v2 topbar */}
          <div className="topbar admin">
            {/* Phone-only (the stylesheet hides it above 860px). The console's
                own door to its navigation, which it did not have. */}
            <button
              type="button"
              className="nav-hamburger"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation menu"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M3 5h14M3 10h14M3 15h14"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="greeting">
              <div className="eyebrow" style={{ fontSize: 10.5 }}>{eyebrow}</div>
              <h1 style={{ fontSize: 26 }}>{title}</h1>
              {sub && (
                <div className="sub" style={{ fontSize: 13 }}>{sub}</div>
              )}
            </div>
          {/* COMPOSITION, NOT REPLACEMENT (2026-08-08) — see the same note in
              screen-shell.tsx. `topbarRight` was a replacement slot, so the six
              admin screens that pass one silently deleted the date pill and the
              ⌘K button. It now renders alongside them.

              This is also where the tenant switcher was missing entirely: it
              lived only in ScreenShell, and /admin never renders ScreenShell —
              so a firm operator holding several workspaces could not reach them
              from the console they actually land on. */}
            <div className="topbar-actions">
              {topbarRight}
              <TenantSwitcher />
                {/* THE DATE PILL IS GONE (2026-08-23). It rendered a live
                    ticking SAST clock on the right of every admin header,
                    including the dashboard — the "second date" Paul asked to
                    remove for the second time. It told an operator nothing they
                    could act on (their own machine already shows the time) and
                    it made the dashboard look like a monitoring console.
                    Timestamps that are DATA — when a document was signed, when
                    an event was logged — are untouched; this was chrome. */}

                {/* Cmd-K trigger. Its ref goes to the palette so the panel
                    drops from THIS button instead of landing in the middle of
                    the viewport (DSH-07). */}
                <button
                  ref={cmdkTriggerRef}
                  data-testid="cmdk-trigger"
                  className="btn-secondary"
                  onClick={openCmdk}
                  style={{ display: 'flex', alignItems: 'center', gap: 7 }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.4" />
                    <path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                  Search
                  <span
                    className="mono"
                    style={{ fontSize: 10, color: 'var(--ink-4)', padding: '1px 5px', border: '1px solid var(--hairline)', borderRadius: 4 }}
                  >
                    ⌘K
                  </span>
                </button>

                {/* Bell — a real popover over the real queues. See
                    NotificationsBell: this was a handler-less button until
                    2026-08-23 and clicking it did nothing at all. */}
                <NotificationsBell />

                {/* Light / dark.
                    THIS IS THE CONTROL PAUL REPORTED AS DEAD, and the confusion
                    was ours: it was labelled "Settings" but drawn as a SUN (a
                    circle with eight rays — the universal light-mode glyph), so
                    it read as a theme toggle to everyone who saw it. It also
                    had no onClick, so it was not a settings control either;
                    it was nothing. It is now the toggle it always looked like,
                    with the label and the icon finally agreeing, and the
                    preference persisted per browser (lib/use-admin-theme.ts). */}
                <button
                  className="icon-btn"
                  type="button"
                  data-testid="theme-toggle"
                  aria-pressed={theme === 'dark'}
                  aria-label={
                    theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
                  }
                  title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                  onClick={toggleTheme}
                  /* The 34px box is `--icon-btn` since phase 1; the inline copy
                     that used to sit here is gone with the rest of the shell's
                     private geometry. */
                >
                  {theme === 'dark' ? (
                    /* In dark, offer the sun (what you would switch TO). */
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                      <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.64 2.64l1.06 1.06M12.3 12.3l1.06 1.06M13.36 2.64l-1.06 1.06M3.7 12.3l-1.06 1.06" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  ) : (
                    /* In light, offer the moon. */
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M13.5 9.6A5.8 5.8 0 0 1 6.4 2.5a5.8 5.8 0 1 0 7.1 7.1Z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
            </div>
          </div>

          {/* v2 scope wrapper */}
          <div className="admin-v2">
            <div className="admin-mobile-notice" role="note">
              The administrator console is designed for a desktop screen. Please open this surface on a laptop or larger for the full layout.
            </div>
            {children}
          </div>
        </main>
      </div>
    </>
  );
}
